import {
  CategorizationTimeoutError,
  ClientRequestAnswerActivity,
  ClientRequestApiService,
  ClientRequestDetails,
  ClientRequestDocumentActivity,
  ClientRequestDocumentFilesCategorizePayload,
  ClientRequestFileSubmissionResult,
  ClientRequestItemStatus,
  ClientRequestMetadataModel,
  ClientRequestModel,
  ClientRequestSubmitFilePayload,
  CreateSecureLinkPayload,
  ItemSubmissionFile,
  MASKED_ITEM_RESPONSE_VALUE,
  ReplaceClientRequestItemSubmissionFilePayload,
  RequestDocument,
  RequestItem,
  RequestSubmissionFile,
  RequestSubmissionFileStatus,
  SecureLinkSendEmailPayload,
  SecureLinkUser,
  UpdateSecureLinkGrantsPayload,
} from '@aiwyn/api';
import { SecureLinkEntityType } from '@aiwyn/auth';
import {
  AddClientRequestMetadataFiles,
  ClientRequestGroupStatus,
  CommonClientRequestsState,
  GetAllClientRequestGroups,
  GetAllClientRequests,
  GetAllDeliverables,
  GetAssignees,
  GetClientRequestDetails,
  GetClientRequestDetailsSuccess,
  GetClientRequestMetadata,
  GetClients,
  GetFiles,
  GetQuestionnaireResponses,
  GetServiceLines,
  GetStaff,
  InsertAnswersIntoRequestItems,
  InsertCategorizedFilesIntoClientRequest,
  InsertUncategorizedFilesIntoClientRequest,
  PatchClientRequestState,
  RemoveClientRequestFilesSuccess,
  RemoveSecureLinkUserForRequestItem,
  REQUEST_IDENTITY_TYPE,
  SetClientRequestItemNotApplicableSuccess,
  SetSecureLinkUserForRequestItem,
  UnCategorizeFilesFromClientRequestDocuments,
} from '@aiwyn/common/client-requests';
import {
  DmsIntegrationState,
  DmsIntegrationStateModel,
  DmsIntegrationStateSelectors,
  isEmpty,
  isLoaded,
  Loadable,
  LoadableArrayLoadStrategy,
  LoadableHelper,
  LoadableStatus,
  Logger,
  LogOut,
  RecursivePartial,
  removeDuplicates,
  removeNestedRecord,
  removeObjectFields,
  ResetState,
  SetFileUploadJobAsError,
  SnackbarService,
  SnackbarType,
  trimObject,
  UploadFileJobCategorizationDisplayStatus,
  UploadFileStateRecord,
  upsertNestedRecord,
} from '@aiwyn/shared';
import { Injectable } from '@angular/core';
import { Action, createPropertySelectors, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import { append, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { combineLatest, defer, EMPTY, merge, Observable, of, throwError } from 'rxjs';
import { catchError, filter, last, map, switchMap, take, takeWhile, tap, timeout } from 'rxjs/operators';
import {
  AllRequestItemFilters,
  ClientRequestDocumentFileDisplay,
  ClientRequestDocumentFileDisplayStatus,
  ClientRequestFilters,
  ClientRequestFilterType,
  ClientRequestItemActivityViewModel,
  getOriginalBoxFileName,
  RequestItemFilters,
  RequestItemStatusFilterOption,
  RequestSubmissionFileStatusToClientRequestDocumentFileDisplayStatus,
} from '../models';
import { ClientRequestTrackingService } from '../services/client-request-tracking.service';
import {
  AppendNewlyUploadedFiles,
  AutoCategorizationSuccess,
  CategorizeFileIntoDocument,
  CategorizeFileIntoDocumentSuccess,
  ClearFocusedDeliverableId,
  ClearRequestItemFilterSelection,
  ClearSelectedClientRequestGroupIdentity,
  ClearSelectedClientRequestId,
  ClearSelectedRequestAsModified,
  ClearStagedFilesForClientRequest,
  ClientRequestLinkFilesToDocuments,
  ClientRequestLinkFilesToDocumentsSuccess,
  ClientRequestSubmitAnswers,
  ClientRequestSubmitAnswersSuccess,
  CreateSecureLink,
  GetSecondaryData,
  InitializeClientRequests,
  LoadAnswerActivity,
  LoadDocumentActivity,
  RemoveNewlyUploadedFiles,
  RemoveStagedFilesForClientRequest,
  ReplaceFileForClientRequest,
  ReplaceFileForClientRequestSuccess,
  SecureLinkSendEmail,
  SetFocusedDeliverableId,
  SetRequestItemAsNotApplicable,
  SetSelectedClientRequestGroupIdentity,
  SetSelectedClientRequestId,
  SetSelectedRequestAsModified,
  SetSelectedRequestItemId,
  StageFilesForClientRequest,
  SubmitClientRequest,
  SubmitClientRequestSuccess,
  SubmitFilesForClientRequest,
  SubmitFilesForClientRequestSuccess,
  UndoFileCategorization,
  UndoFileCategorizationSuccess,
  UpdateFilter,
  UpdateRequestItemFilterSelection,
  UpdateSecureLinkGrants,
} from './client-requests.actions';
import {
  CLIENT_REQUEST_FILTERS_KEY,
  CLIENT_REQUEST_ITEM_FILTERS_KEY,
  ClientRequestsStateModel,
  DEFAULT_CLIENT_REQUESTS_STATE,
} from './client-requests.model';
import { getFlaggedFilesIds } from './utils/flagged-files.utils';
import { calculateClientRequestListItemsTotals, RequestListTotalsData } from './utils/request-list-totals';

export const CLIENT_REQUESTS_STATE_SLICE_NAME = 'clientRequests';
const FILE_CATEGORIZATION_TIMEOUT = 60000;

@Injectable({ providedIn: 'root' })
@State<ClientRequestsStateModel>({
  name: CLIENT_REQUESTS_STATE_SLICE_NAME,
  defaults: DEFAULT_CLIENT_REQUESTS_STATE,
})
export class ClientRequestsState {
  static readonly getSlices = createPropertySelectors<ClientRequestsStateModel>(ClientRequestsState);

  static getFilter(
    filterType: ClientRequestFilterType,
  ): (filters: ClientRequestFilters) => (number | ClientRequestGroupStatus | RequestItemStatusFilterOption)[] {
    return createSelector([ClientRequestsState.getSlices.filters], (filters) => filters[filterType]);
  }

  @Selector([CommonClientRequestsState.getAllClientRequestsLoadables, ClientRequestsState.getSlices.selectedRequestId])
  static getSelectedClientRequestLoadable(
    requests: Loadable<ClientRequestModel>[],
    selectedRequestId: number,
  ): Loadable<ClientRequestModel> {
    return CommonClientRequestsState.getClientRequestLoadableHelper(requests, selectedRequestId);
  }

  @Selector([ClientRequestsState.getSelectedClientRequestLoadable])
  static getSelectedClientRequestStatus({ status }: Loadable<ClientRequestModel>): LoadableStatus {
    return status;
  }

  @Selector([ClientRequestsState.getSelectedClientRequestLoadable])
  static getSelectedClientRequest({ data }: Loadable<ClientRequestModel>): ClientRequestModel {
    return data;
  }

  @Selector([CommonClientRequestsState.getAllClientRequestDetailsLoadables, ClientRequestsState.getSlices.selectedRequestId])
  static getSelectedClientRequestDetailsLoadable(
    details: Loadable<ClientRequestDetails>[],
    selectedRequestId: number,
  ): Loadable<ClientRequestDetails> {
    return CommonClientRequestsState.getClientRequestDetailsLoadableHelper(details, selectedRequestId);
  }

  @Selector([ClientRequestsState.getSelectedClientRequestDetailsLoadable])
  static getSelectedClientRequestDetailsStatus(selectedClientRequestDetails: Loadable<ClientRequestDetails>): LoadableStatus {
    return selectedClientRequestDetails?.status;
  }

  @Selector([ClientRequestsState.getSelectedClientRequestDetailsLoadable])
  static getSelectedClientRequestDetails(selectedClientRequestDetails: Loadable<ClientRequestDetails>): ClientRequestDetails {
    return selectedClientRequestDetails?.data;
  }

  @Selector([CommonClientRequestsState.getAllClientRequestMetadataLoadables, ClientRequestsState.getSlices.selectedRequestId])
  static getSelectedClientRequestMetadataLoadable(
    metadata: Loadable<ClientRequestMetadataModel>[],
    selectedRequestId: number,
  ): Loadable<ClientRequestMetadataModel> {
    return CommonClientRequestsState.getClientRequestMetadataLoadableHelper(metadata, selectedRequestId);
  }

  @Selector([ClientRequestsState.getSelectedClientRequestMetadataLoadable])
  static getSelectedClientRequestMetadataStatus(selectedClientRequestMetadata: Loadable<ClientRequestMetadataModel>): LoadableStatus {
    return selectedClientRequestMetadata?.status;
  }

  @Selector([ClientRequestsState.getSelectedClientRequestMetadataLoadable])
  static getSelectedClientRequestMetadata(selectedClientRequestMetadata: Loadable<ClientRequestMetadataModel>): ClientRequestMetadataModel {
    return selectedClientRequestMetadata?.data;
  }

  /**
   * NOTE: External state added due to Refactor
   * TODO: Replace this with a new selector in a new file to avoid this pattern
   */
  @Selector([ClientRequestsState.getSlices.stagedFileJobIds, DmsIntegrationState])
  static getStagedFileJobs(stagedJobIds: string[], dmsState: DmsIntegrationStateModel): UploadFileStateRecord[] {
    return stagedJobIds
      .map((jobId) => dmsState.fileUploads.find((job) => job.jobId === jobId))
      .filter((job): job is UploadFileStateRecord => job != null);
  }

  static getStagedFileByJobId(jobId: string): (stagedJobs: UploadFileStateRecord[]) => UploadFileStateRecord {
    return createSelector([ClientRequestsState.getStagedFileJobs], (stagedJobs: UploadFileStateRecord[]) => {
      return stagedJobs.find((job) => job.jobId === jobId) ?? null;
    });
  }

  @Selector([ClientRequestsState.getSelectedClientRequestDetails])
  static getSelectedClientRequestItems(details: ClientRequestDetails): { sectionId: number; itemId: number; item: RequestItem }[] {
    return (details?.sections ?? []).flatMap((section) => {
      return (section.items ?? []).map((item) => ({ sectionId: section.id, itemId: item.id, item }));
    });
  }

  static getSelectedClientRequestItemById(
    itemId: number,
  ): (items: { sectionId: number; itemId: number; item: RequestItem }[]) => RequestItem | null {
    return createSelector([ClientRequestsState.getSelectedClientRequestItems], (items) => {
      const foundItem = items.find(({ itemId: id }) => id === itemId);
      return foundItem ? foundItem.item : null;
    });
  }

  @Selector([ClientRequestsState.getSlices.documentActivity, ClientRequestsState.getSelectedClientRequestDetails])
  static getFlaggedFileIds(documentActivity: ClientRequestDocumentActivity[], details: ClientRequestDetails): number[] {
    return getFlaggedFilesIds(documentActivity, details);
  }

  @Selector([ClientRequestsState.getSelectedClientRequestDetails])
  static getFileToRequestItemMapping(details: ClientRequestDetails): Map<number, number> {
    const fileToRequestItemMap = new Map<number, number>();
    const processedFileIds = new Set<number>();

    // Collect all unique fileIds from two sources:
    // 1. Item structure (details.sections[].items[].files[]) - files currently linked to items
    // 2. Request-level files (details.files[]) - includes uncategorized and unlinked files
    const allFileIds = new Set<number>();

    // Collect fileIds from item structure (item-linked files)
    for (const section of details?.sections ?? []) {
      for (const item of section.items ?? []) {
        const requestDocument = item as RequestDocument;
        if (requestDocument.files) {
          requestDocument.files.forEach((itemFile) => {
            allFileIds.add(itemFile.fileId);
          });
        }
      }
    }

    // Also collect fileIds from details.files[] (includes uncategorized and unlinked files as well)
    for (const file of details?.files ?? []) {
      allFileIds.add(file.fileId);
    }

    // Process each unique fileId to find its primary request item
    for (const fileId of allFileIds) {
      if (processedFileIds.has(fileId)) {
        continue;
      }
      processedFileIds.add(fileId);

      // Find ALL RequestItems (documents) this file belongs to
      const matchingRequestItems: Array<{
        id: number;
        sectionOrdinal: number;
        itemOrdinal: number;
      }> = [];

      // Look through all sections and items to collect ALL matches
      for (const section of details?.sections ?? []) {
        for (const item of section.items ?? []) {
          // Check if this item has files and if any file matches our fileId
          const requestDocument = item as RequestDocument;
          if (requestDocument.files) {
            const matchingFile = requestDocument.files.find((itemFile) => itemFile.fileId === fileId);
            if (matchingFile) {
              matchingRequestItems.push({
                id: item.id,
                sectionOrdinal: section.ordinal,
                itemOrdinal: item.ordinal,
              });
            }
          }
        }
      }

      // Sort by section ordinal first, then item ordinal, and select the first
      const primaryRequestItem = matchingRequestItems.sort((a, b) => {
        if (a.sectionOrdinal !== b.sectionOrdinal) {
          return a.sectionOrdinal - b.sectionOrdinal;
        }
        return a.itemOrdinal - b.itemOrdinal;
      })[0];

      // Store the mapping (fileId -> requestItemId)
      if (primaryRequestItem) {
        fileToRequestItemMap.set(fileId, primaryRequestItem.id);
      }
    }

    return fileToRequestItemMap;
  }

  @Selector([ClientRequestsState.getSelectedClientRequestDetails])
  static getFileToRequestItemsWithStatusMapping(
    details: ClientRequestDetails,
  ): Map<number, Array<{ requestItemId: number; status: ClientRequestItemStatus }>> {
    const fileToItemsMap = new Map<number, Array<{ requestItemId: number; status: ClientRequestItemStatus }>>();

    // Process each file in the details to find ALL linked request items with their statuses
    for (const file of details?.files ?? []) {
      const linkedItems: Array<{ requestItemId: number; status: ClientRequestItemStatus }> = [];

      // Look through all sections and items to collect ALL matches
      for (const section of details?.sections ?? []) {
        for (const item of section.items ?? []) {
          // Check if this item has files and if any file matches our fileId
          const requestDocument = item as RequestDocument;
          if (requestDocument.files) {
            const matchingFile = requestDocument.files.find((itemFile) => itemFile.fileId === file.fileId);
            if (matchingFile) {
              linkedItems.push({
                requestItemId: item.id,
                status: item.status,
              });
            }
          }
        }
      }

      // Store all linked items for this file
      if (linkedItems.length > 0) {
        fileToItemsMap.set(file.fileId, linkedItems);
      }
    }

    return fileToItemsMap;
  }

  @Selector([ClientRequestsState.getStagedFileJobs])
  static getStagedFileDisplays(stagedJobs: UploadFileStateRecord[]): ClientRequestDocumentFileDisplay[] {
    return stagedJobs.map((job) => ({
      id: undefined,
      name: job.data.file.name,
      status: job.data.status,
      dmsId: job.identity as string,
      dmsUploadFileJobId: job.jobId,
      isMasked: job.data.file.name === MASKED_ITEM_RESPONSE_VALUE,
    }));
  }

  @Selector([
    ClientRequestsState.getSelectedClientRequestDetails,
    ClientRequestsState.getSelectedClientRequestMetadata,
    ClientRequestsState.getFileToRequestItemMapping,
  ])
  static getUploadedFileDisplays(
    details: ClientRequestDetails,
    metadata: ClientRequestMetadataModel,
    fileToRequestItemMap: Map<number, number>,
  ): ClientRequestDocumentFileDisplay[] {
    const fileDisplays: ClientRequestDocumentFileDisplay[] = [];
    const processedFileIds = new Set<number>();

    // First, process files from details.files[] (includes all request-level files)
    (details?.files ?? []).forEach((file) => {
      processedFileIds.add(file.fileId);

      const rawFile = (metadata?.files ?? []).find(
        (requestSubmissionFile) => requestSubmissionFile.file?.id === file.fileId || requestSubmissionFile.fileId === file.fileId,
      );

      // Get the primary request item ID from the mapping
      const requestItemId = fileToRequestItemMap.get(file.fileId) || null;
      const sections = details?.sections ?? [];
      sections.reduce(
        (foundItem, section) => foundItem || section.items.find((item) => item.id === requestItemId),
        undefined as RequestItem | undefined,
      );

      fileDisplays.push({
        id: file.fileId,
        name: rawFile?.file?.name ? getOriginalBoxFileName(rawFile?.file?.name) : '** File not found **',
        status: RequestSubmissionFileStatusToClientRequestDocumentFileDisplayStatus[file.status],
        dmsId: rawFile?.file?.dmsId ?? rawFile?.file?.dmsFileId,
        requestItemId,
        isMasked: rawFile?.file?.name === MASKED_ITEM_RESPONSE_VALUE,
        createdAt: file.createdAt,
      });
    });

    // Second, process files from metadata.files[] that aren't already in details.files[]
    // This handles item-linked files visible to the user but not in details.files[] (thanks to API chaining)
    (metadata?.files ?? []).forEach((metadataFile) => {
      if (!processedFileIds.has(metadataFile.fileId)) {
        processedFileIds.add(metadataFile.fileId);

        const requestItemId = fileToRequestItemMap.get(metadataFile.fileId) || null;
        const sections = details?.sections ?? [];
        sections.reduce(
          (foundItem, section) => foundItem || section.items.find((item) => item.id === requestItemId),
          undefined as RequestItem | undefined,
        );

        fileDisplays.push({
          id: metadataFile.fileId,
          name: metadataFile.file?.name ? getOriginalBoxFileName(metadataFile.file.name) : '** File not found **',
          status: RequestSubmissionFileStatusToClientRequestDocumentFileDisplayStatus[metadataFile.status],
          dmsId: metadataFile.file?.dmsId ?? metadataFile.file?.dmsFileId,
          requestItemId,
          isMasked: metadataFile.file?.name === MASKED_ITEM_RESPONSE_VALUE,
          createdAt: metadataFile.createdAt,
        });
      }
    });

    return fileDisplays;
  }

  @Selector([ClientRequestsState.getUploadedFileDisplays])
  static getCategorizedFileDisplays(uploadedFiles: ClientRequestDocumentFileDisplay[]): ClientRequestDocumentFileDisplay[] {
    return uploadedFiles.filter((file) => file.status === ClientRequestDocumentFileDisplayStatus.CATEGORIZED);
  }

  @Selector([ClientRequestsState.getUploadedFileDisplays])
  static getUncategorizedFileDisplays(uploadedFiles: ClientRequestDocumentFileDisplay[]): ClientRequestDocumentFileDisplay[] {
    return uploadedFiles.filter((file) => file.status === ClientRequestDocumentFileDisplayStatus.UNCATEGORIZED);
  }

  /**
   * Creates a map of submission file data using composite keys.
   *
   * Uses `${fileId}-${documentId}` keys because the same file can be linked to multiple documents,
   * and simple fileId keys would cause data loss when entries overwrite each other.
   *
   * @example
   * ```typescript
   * const submissionData = submissionFileDataMap.get(`${fileId}-${documentId}`);
   * ```
   *
   * @param details - Client request details containing sections, items, and files
   * @returns Map with composite keys (fileId-documentId) pointing to submission file data
   */
  @Selector([ClientRequestsState.getSelectedClientRequestDetails])
  static getSubmissionFileDataMap(details: ClientRequestDetails): Map<string, ItemSubmissionFile> {
    const submissionFileDataMap = new Map<string, ItemSubmissionFile>();

    if (details?.sections) {
      details.sections.forEach((section) => {
        section.items.forEach((item) => {
          if (item.files) {
            item.files.forEach((file) => {
              const compositeKey = ClientRequestsState.getSubmissionFileCompositeKey(file, item.id);
              submissionFileDataMap.set(compositeKey, file);
            });
          }
        });
      });
    }

    return submissionFileDataMap;
  }

  static getSubmissionFileCompositeKey(
    file: RequestSubmissionFile | { documentId: number; fileId: number; id: number; status: RequestSubmissionFileStatus },
    documentId: number,
  ): string {
    if (!('file' in file)) {
      return `${file.fileId}-${documentId}`;
    }

    return file.file?.['isMasked'] ? `masked_${file.id}-${documentId}` : `${file.fileId}-${documentId}`;
  }

  @Selector([ClientRequestsState.getSelectedClientRequestDetails])
  static getFlaggedFilesWithRequestItems(
    details: ClientRequestDetails,
  ): (ClientRequestDocumentFileDisplay & { requestItemName: string })[] {
    if (!details?.sections) {
      return [];
    }

    const flaggedFiles: (ClientRequestDocumentFileDisplay & { requestItemName: string })[] = [];

    // Extract  files from sections → items → files structure (single source of iteration)
    details.sections.forEach((section) => {
      section.items.forEach((item) => {
        if (item.files) {
          item.files.forEach((file) => {
            if (file.isFlagged) {
              flaggedFiles.push({
                id: file.fileId,
                dmsId: file.file?.dmsId || '',
                name: file.file?.name || 'Unknown File',
                status: ClientRequestDocumentFileDisplayStatus.CATEGORIZED,
                isFlagged: file.isFlagged,
                requestItemId: item.id,
                requestItemName: item.name,
                isMasked: file.file.name === MASKED_ITEM_RESPONSE_VALUE,
              });
            }
          });
        }
      });
    });

    return flaggedFiles;
  }

  @Selector([ClientRequestsState.getStagedFileDisplays, ClientRequestsState.getUploadedFileDisplays])
  static getAllFileDisplays(
    stagedFiles: ClientRequestDocumentFileDisplay[],
    uploadedFileDisplays: ClientRequestDocumentFileDisplay[],
  ): ClientRequestDocumentFileDisplay[] {
    return stagedFiles.concat(uploadedFileDisplays);
  }

  @Selector([ClientRequestsState.getSlices.answersActivity, ClientRequestsState.getSlices.documentActivity])
  static getItemActivityViewModels(
    answerActivity: ClientRequestAnswerActivity[],
    documentActivity: ClientRequestDocumentActivity[],
  ): ClientRequestItemActivityViewModel[] {
    // Transform answer activities to view models
    const answerViewModels: ClientRequestItemActivityViewModel[] = answerActivity.map((activity) => ({
      ...activity,
      activityType: 'answer' as const,
    }));

    // Transform document activities to view models
    const documentViewModels: ClientRequestItemActivityViewModel[] = documentActivity.map((activity) => ({
      itemId: activity.itemId,
      itemName: activity.itemName,
      rejectedAt: activity.rejectedAt,
      status: activity.status,
      submittedAt: activity.submittedAt,
      // Normalize snake_case to camelCase
      userId: activity.user_id,
      userName: activity.user_name,
      userType: activity.user_type,
      rejectionNotes: activity.rejection_notes,
      // Document-specific fields
      activityType: 'document' as const,
      notes: activity.notes,
      files: activity.files,
    }));

    // Combine and sort by chronological order (most recent first)
    const allActivities = [...answerViewModels, ...documentViewModels];
    return allActivities.sort((a, b) => {
      const dateA = new Date(a.rejectedAt || a.submittedAt).getTime();
      const dateB = new Date(b.rejectedAt || b.submittedAt).getTime();
      return dateB - dateA; // Descending order
    });
  }

  private static buildFileFlagMap(details: ClientRequestDetails): Map<number, boolean> {
    const map = new Map<number, boolean>();
    (details?.sections ?? []).forEach((section) => {
      section.items.forEach((item) => {
        ((item as Partial<RequestDocument>).files ?? []).forEach((file) => {
          const fileId = file.file?.id ?? file.fileId;
          if (fileId != null) {
            map.set(fileId, file.isFlagged ?? false);
          }
        });
      });
    });
    return map;
  }

  @Selector([ClientRequestsState.getCategorizedFileDisplays, ClientRequestsState.getSelectedClientRequestDetails])
  static getCategorizedFileDisplaysWithFlagging(
    categorizedFiles: ClientRequestDocumentFileDisplay[],
    details: ClientRequestDetails,
  ): ClientRequestDocumentFileDisplay[] {
    const flaggingMap = ClientRequestsState.buildFileFlagMap(details);
    return categorizedFiles.map((file) => ({
      ...file,
      isFlagged: flaggingMap.get(file.id) ?? false,
    }));
  }

  @Selector([ClientRequestsState.getSlices.notApplicableItems, ClientRequestsState.getSlices.selectedRequestId])
  static getNotApplicableItemsForSelectedRequest(
    notApplicableItems: { [clientRequestId: number]: Array<{ itemId: number; note: string | null }> },
    selectedRequestId: number,
  ): number[] {
    return (notApplicableItems[selectedRequestId] || []).map((item) => item.itemId);
  }

  @Selector([ClientRequestsState.getSelectedClientRequestDetails, ClientRequestsState.getSelectedRequestItemFilters])
  static getSelectedRequestItemStatusFilterDetails(
    details: ClientRequestDetails,
    itemFilters: RequestItemFilters,
  ): RequestListTotalsData[] {
    return calculateClientRequestListItemsTotals(details, itemFilters);
  }

  static getNotApplicableItemsForRequest(clientRequestId: number) {
    return createSelector(
      [ClientRequestsState.getSlices.notApplicableItems],
      (notApplicableItems: { [clientRequestId: number]: Array<{ itemId: number; note: string | null }> }) =>
        (notApplicableItems[clientRequestId] || []).map((item) => item.itemId),
    );
  }

  static isItemNotApplicable(clientRequestId: number, itemId: number) {
    return createSelector(
      [ClientRequestsState.getSlices.notApplicableItems],
      (notApplicableItems: { [clientRequestId: number]: Array<{ itemId: number; note: string | null }> }) =>
        (notApplicableItems[clientRequestId] || []).some((item) => item.itemId === itemId),
    );
  }

  static getNotApplicableNote(clientRequestId: number, requestItemId: number) {
    return createSelector(
      [ClientRequestsState.getSlices.notApplicableItems],
      (notApplicableItems: { [clientRequestId: number]: Array<{ itemId: number; note: string | null }> }) =>
        (notApplicableItems[clientRequestId] || []).find((item) => item.itemId === requestItemId)?.note || null,
    );
  }

  @Selector([ClientRequestsState.getSlices.selectedRequestId, ClientRequestsState.getSlices.requestItemFilters])
  static getSelectedRequestItemFilters(
    selectedRequestId: number,
    filters: AllRequestItemFilters | undefined,
  ): RecursivePartial<RequestItemFilters> | undefined {
    return filters?.[selectedRequestId];
  }

  @Selector([ClientRequestsState.getSelectedRequestItemFilters])
  static doesSelectedRequestHaveActiveItemFilters(filters: RecursivePartial<RequestItemFilters> | undefined): boolean {
    if (!filters) {
      return false;
    }

    // filters.isApplicable // TODO: May be implemented in the future
    return Object.values(filters.status ?? {}).some(Boolean) || Object.values(filters.dueDate ?? {}).some(Boolean);
  }

  constructor(
    private readonly store: Store,
    private readonly logger: Logger,
    private readonly clientRequestApiService: ClientRequestApiService,
    private readonly snackbarService: SnackbarService,
    private readonly clientRequestPendoTrackingService: ClientRequestTrackingService,
  ) {}

  @Action([InitializeClientRequests])
  initializeClientRequests({ dispatch, patchState }: StateContext<ClientRequestsStateModel>): Observable<unknown> {
    const status = this.store.selectSnapshot(ClientRequestsState.getSlices.status);
    if (!LoadableHelper.shouldRequest(status)) {
      return this.store.select(ClientRequestsState.getSlices.status).pipe(isLoaded(true));
    }

    patchState({ status: LoadableStatus.LOADING });

    // Load filters from local storage if present
    const requestFilters = JSON.parse(localStorage.getItem(CLIENT_REQUEST_FILTERS_KEY) ?? '{}');
    if (!isEmpty(requestFilters)) {
      patchState({ filters: requestFilters });
    }

    const itemFilters = JSON.parse(localStorage.getItem(CLIENT_REQUEST_ITEM_FILTERS_KEY) ?? '{}');
    if (!isEmpty(itemFilters)) {
      patchState({ requestItemFilters: itemFilters });
    }

    return dispatch([
      new GetAllClientRequestGroups(LoadableArrayLoadStrategy.ADD),
      new GetAllClientRequests(LoadableArrayLoadStrategy.ADD),
    ]).pipe(
      switchMap((_) => dispatch(new GetSecondaryData())),
      tap(
        (_) => patchState({ status: LoadableStatus.LOADED }),
        (error) => {
          this.logger.error('ClientRequestsState : InitializeClientRequests : Error', error);
          patchState({ status: LoadableStatus.ERROR });
        },
      ),
    );
  }

  @Action([GetSecondaryData])
  getSecondaryData({ dispatch }: StateContext<ClientRequestsStateModel>, { loadStrategy }: GetSecondaryData): Observable<unknown> {
    return combineLatest([
      dispatch([new GetAssignees(loadStrategy), new GetStaff(loadStrategy), new GetClients(loadStrategy), new GetServiceLines()]),
      dispatch(new GetAllDeliverables()).pipe(
        map((_) => {
          const allDeliverables = this.store.selectSnapshot(CommonClientRequestsState.getAllDeliverables);
          // get all file Ids from all deliverables
          const fileIds = allDeliverables.reduce((acc, deliverable) => acc.concat(deliverable.fileIds), [] as number[]);
          return removeDuplicates(fileIds);
        }),
        switchMap((fileIds) => dispatch(new GetFiles(fileIds))),
      ),
    ]);
  }

  @Action([UpdateFilter])
  updateFilter({ patchState }: StateContext<ClientRequestsStateModel>, { type, value }: UpdateFilter): void {
    const existingFilters = this.store.selectSnapshot(ClientRequestsState.getSlices.filters);
    const otherFilters = removeObjectFields(existingFilters, [type]);
    const updatedMatchingFilter = isEmpty(value) ? {} : { [type]: value };
    const updatedFilters = { ...otherFilters, ...updatedMatchingFilter };

    patchState({ filters: updatedFilters });
    if (isEmpty(updatedFilters)) {
      localStorage.removeItem(CLIENT_REQUEST_FILTERS_KEY);
    } else {
      localStorage.setItem(CLIENT_REQUEST_FILTERS_KEY, JSON.stringify(updatedFilters));
    }
  }

  @Action([SetSelectedClientRequestGroupIdentity])
  setSelectedClientRequestGroupId(
    { patchState }: StateContext<ClientRequestsStateModel>,
    { groupIdentity }: SetSelectedClientRequestGroupIdentity,
  ): void {
    patchState({ selectedGroupIdentity: groupIdentity });
  }

  @Action([ClearSelectedClientRequestGroupIdentity])
  clearSelectedClientRequestGroupIdentity({ patchState }: StateContext<ClientRequestsStateModel>): void {
    patchState({ selectedGroupIdentity: undefined });
  }

  @Action([SetSelectedClientRequestId])
  setSelectedClientRequestId(
    { patchState }: StateContext<ClientRequestsStateModel>,
    { requestId, requestItemIds }: SetSelectedClientRequestId,
  ): Observable<unknown> {
    const selectedRequestId = +(requestId + '').replace(REQUEST_IDENTITY_TYPE, '').replace('-', '');

    patchState({
      selectedRequestId,
      restrictedRequestItemsIds: requestItemIds,
    });

    const selectedRequestDetails = this.store.selectSnapshot(CommonClientRequestsState.getClientRequestDetailsLoadable(requestId));
    const getClientRequestDetails$ = (
      selectedRequestDetails
        ? this.store.select(ClientRequestsState.getSelectedClientRequestDetailsStatus).pipe(isLoaded(true), take(1))
        : defer(() => this.store.dispatch(new GetClientRequestDetails(requestId, LoadableArrayLoadStrategy.REPLACE, requestItemIds)))
    ) as Observable<unknown>;

    const selectedRequestMetadata = this.store.selectSnapshot(CommonClientRequestsState.getClientRequestMetadataLoadable(requestId));
    const getClientRequestMetadata$ = (
      selectedRequestMetadata
        ? this.store.select(ClientRequestsState.getSelectedClientRequestMetadataStatus).pipe(isLoaded(true), take(1))
        : defer(() => this.store.dispatch(new GetClientRequestMetadata(requestId)))
    ) as Observable<unknown>;

    // Get questionnaire responses for the selected client request
    const getQuestionnaireResponses$ = defer(() => this.store.dispatch(new GetQuestionnaireResponses(requestId))) as Observable<unknown>;

    // Wait for details to load, then trigger metadata and questionnaire fetch in parallel
    return getClientRequestDetails$.pipe(switchMap(() => combineLatest([getClientRequestMetadata$, getQuestionnaireResponses$])));
  }

  @Action([SetFocusedDeliverableId])
  setFocusedDeliverableId({ patchState }: StateContext<ClientRequestsStateModel>, { deliverableId }: SetFocusedDeliverableId): void {
    patchState({ focusedDeliverableId: deliverableId });
  }

  @Action([ClearFocusedDeliverableId])
  clearFocusedDeliverableId({ patchState }: StateContext<ClientRequestsStateModel>): void {
    patchState({ focusedDeliverableId: undefined });
  }

  @Action([ClearSelectedClientRequestId])
  clearSelectedClientRequestId({ patchState }: StateContext<ClientRequestsStateModel>): void {
    patchState({
      selectedRequestId: undefined,
      selectedRequestItemId: undefined,
      restrictedRequestItemsIds: undefined,
      answersActivity: [],
      documentActivity: [],
    });
  }

  @Action([LoadAnswerActivity])
  LoadAnswerActivity({ patchState }: StateContext<ClientRequestsStateModel>, { itemIds }: LoadAnswerActivity): Observable<unknown> {
    return this.clientRequestApiService
      .getAnswerActivity(itemIds)
      .pipe(tap((answersActivity) => patchState({ answersActivity: answersActivity as ClientRequestAnswerActivity[] })));
  }

  @Action([LoadDocumentActivity])
  loadDocumentActivity({ patchState }: StateContext<ClientRequestsStateModel>, { itemIds }: LoadDocumentActivity): Observable<unknown> {
    return this.clientRequestApiService
      .getDocumentActivity(itemIds)
      .pipe(tap((documentActivity) => patchState({ documentActivity: documentActivity as ClientRequestDocumentActivity[] })));
  }

  @Action([StageFilesForClientRequest])
  stageFilesForClientRequest(
    { getState, patchState }: StateContext<ClientRequestsStateModel>,
    { jobIds }: StageFilesForClientRequest,
  ): void {
    this._setSelectedRequestAsModifiedIfNotAlready();

    const currentJobIds = getState().stagedFileJobIds;
    patchState({
      stagedFileJobIds: [...currentJobIds, ...jobIds],
    });
  }

  @Action([RemoveStagedFilesForClientRequest])
  removeStagedFilesForClientRequest(
    { getState, patchState }: StateContext<ClientRequestsStateModel>,
    { jobIdsToRemove }: RemoveStagedFilesForClientRequest,
  ): void {
    const currentJobIds = getState().stagedFileJobIds;
    const remainingJobIds = currentJobIds.filter((jobId) => !jobIdsToRemove.includes(jobId));
    patchState({ stagedFileJobIds: remainingJobIds });
  }

  @Action([ClearStagedFilesForClientRequest])
  clearStagedFilesForClientRequest({ patchState }: StateContext<ClientRequestsStateModel>): void {
    patchState({ stagedFileJobIds: [] });
  }

  @Action([SubmitFilesForClientRequest])
  submitFilesForClientRequest(
    context: StateContext<ClientRequestsStateModel>,
    { files }: SubmitFilesForClientRequest,
  ): Observable<unknown> {
    const selectedClientRequestId = this.store.selectSnapshot(ClientRequestsState.getSlices.selectedRequestId);
    const filesData = files
      .map((fileData) => {
        const fileJob = this.store.selectSnapshot(DmsIntegrationStateSelectors.getUploadFileJobByJobId(fileData?.jobId));
        if (!fileJob?.data?.fileExternalId) {
          return null;
        }
        return { fileJob, fileData, jobId: fileData?.jobId };
      })
      .filter((o) => o);

    this._setSelectedRequestAsModifiedIfNotAlready();

    const variables: ClientRequestSubmitFilePayload = {
      clientRequestId: selectedClientRequestId,
      clientRequestDocumentFiles: filesData.map(({ fileJob, fileData }) => {
        return {
          fileDmsId: fileJob.data.fileExternalId,
          fileName: fileJob.data.file.name,
          fileUrl: '',
          documentIds: fileData.documentIds ?? [],
          skipCategorization: !!fileData.skipCategorization,
        };
      }),
    };

    return this.clientRequestApiService.submitFileToClientRequest(variables).pipe(
      catchError((error: unknown) => {
        this.logger.error('ClientRequestsState : SubmitFilesForClientRequest : File submission failed', error);

        const readableMessage = this._getSubmitFileReadableErrorMessage(error);
        const jobIds = filesData.map((fd) => fd.jobId);

        jobIds.forEach((jobId) => {
          context.dispatch(
            new SetFileUploadJobAsError(jobId, {
              code: 0,
              message: readableMessage,
            }),
          );
        });

        if (jobIds.length) {
          context.dispatch(new RemoveStagedFilesForClientRequest(jobIds));
        }

        this.snackbarService.open(readableMessage, SnackbarType.ERROR);

        return EMPTY;
      }),
      switchMap((filesSubmissionResult) => {
        // update staged files status
        for (const file of filesSubmissionResult) {
          if (file.submissionFileStatus === RequestSubmissionFileStatus.CATEGORIZING) {
            const fileData = filesData.find((fd) => fd.fileJob.data.fileExternalId === file.fileDmsId);
            if (fileData) {
              const newStatus = RequestSubmissionFileStatusToClientRequestDocumentFileDisplayStatus[file.submissionFileStatus];
              context.dispatch(new UploadFileJobCategorizationDisplayStatus(fileData.jobId, newStatus));
            }
          }
        }

        const jobIdsToRemove: string[] = this.store
          .selectSnapshot(ClientRequestsState.getSlices.stagedFileJobIds)
          .map((stagedJobId) => filesData.find((fd) => fd.jobId === stagedJobId))
          .filter((fd) => fd)
          .map((fileData) => {
            const fileSubmissionResult = filesSubmissionResult.find((fsr) => fsr.fileDmsId === fileData.fileJob.data.fileExternalId);
            if (fileSubmissionResult.submissionFileStatus !== RequestSubmissionFileStatus.CATEGORIZING) {
              return fileData.jobId;
            }
          })
          .filter((o) => o);

        const categorizedFilesToInsert: ItemSubmissionFile[] = filesSubmissionResult
          .filter(({ documents }) => !isEmpty(documents))
          .reduce(
            (acc, item) =>
              acc.concat(
                item.documents.map((document) => ({
                  id: document.id,
                  documentId: document.documentId,
                  fileId: item.fileId,
                  status: item.submissionFileStatus,
                })),
              ),
            [] as ItemSubmissionFile[],
          );

        const allFilesToInsert: RequestSubmissionFile[] = filesSubmissionResult.map((item) => ({
          id: item.submissionFileId,
          clientRequestId: selectedClientRequestId,
          fileId: item.fileId,
          status: item.submissionFileStatus,
          file: {
            id: item.fileId,
            dmsId: item.fileDmsId,
          },
        }));

        const newFileNames = filesData.map((fd) => getOriginalBoxFileName(fd.fileJob.data.fileName));

        const listOfActions = [
          new RemoveStagedFilesForClientRequest(jobIdsToRemove),
          new AddClientRequestMetadataFiles(
            selectedClientRequestId,
            filesSubmissionResult.map(({ fileId }) => fileId),
          ),
          new AppendNewlyUploadedFiles(newFileNames),
        ];

        const filesUploadedDirectlyToClientRequest = filesSubmissionResult.filter(
          (file) =>
            // Check if the file was uploaded directly to the client request and if the skipCategorization flag is not set
            (!file.documents || !file.documents.length) &&
            filesData.some((fd) => fd.fileJob.data.fileExternalId === file.fileDmsId && !fd.fileData.skipCategorization),
        );

        if (filesUploadedDirectlyToClientRequest.length > 0) {
          context.dispatch(listOfActions);
          return this._handleFilesUploadedDirectlyToClientRequest(
            filesUploadedDirectlyToClientRequest,
            context,
            selectedClientRequestId,
          ).pipe(tap(() => context.dispatch(new SubmitFilesForClientRequestSuccess())));
        }

        return context.dispatch([
          ...listOfActions,
          new InsertCategorizedFilesIntoClientRequest(selectedClientRequestId, categorizedFilesToInsert),
          new InsertUncategorizedFilesIntoClientRequest(selectedClientRequestId, allFilesToInsert),
          new SubmitFilesForClientRequestSuccess(),
        ]);
      }),
    );
  }

  @Action([ClientRequestLinkFilesToDocuments])
  clientRequestLinkFilesToDocuments(
    { dispatch }: StateContext<ClientRequestsStateModel>,
    { linkFileToDocumentsRequestsPayload }: ClientRequestLinkFilesToDocuments,
  ): Observable<boolean> {
    return this.clientRequestApiService.clientRequestLinkFilesToDocuments(linkFileToDocumentsRequestsPayload).pipe(
      tap((success: boolean) => {
        if (!success) {
          this.snackbarService.open('There was a problem updating the links.', SnackbarType.WARNING);
        } else {
          dispatch(new ClientRequestLinkFilesToDocumentsSuccess());
        }
      }),
    );
  }

  @Action([CategorizeFileIntoDocument])
  categorizeFileIntoDocument(
    _: StateContext<ClientRequestsStateModel>,
    { clientRequestDocumentFilesCategorizePayload }: CategorizeFileIntoDocument,
  ): Observable<unknown> {
    this._setSelectedRequestAsModifiedIfNotAlready();
    const categorizePayload: ClientRequestDocumentFilesCategorizePayload = {
      ...clientRequestDocumentFilesCategorizePayload,
      clientRequestDocumentFileCategorizeList: clientRequestDocumentFilesCategorizePayload.clientRequestDocumentFileCategorizeList.map(
        (file) => ({
          fileId: file.fileId,
          documentId: file.documentId,
        }),
      ),
    };

    return this.clientRequestApiService.categorizeFilesIntoDocuments(categorizePayload).pipe(
      switchMap((fileCategorizationResult: ItemSubmissionFile[]) => {
        const selectedRequestId = this.store.selectSnapshot(ClientRequestsState.getSlices.selectedRequestId);
        return this.store.dispatch([
          new InsertCategorizedFilesIntoClientRequest(selectedRequestId, fileCategorizationResult),
          new AppendNewlyUploadedFiles(
            clientRequestDocumentFilesCategorizePayload.clientRequestDocumentFileCategorizeList.map((file) => file.fileName),
          ),
          new CategorizeFileIntoDocumentSuccess(),
        ]);
      }),
    );
  }

  @Action([ClientRequestSubmitAnswers])
  clientRequestSubmitAnswers(
    _: StateContext<ClientRequestsStateModel>,
    { submitAnswerPayload }: ClientRequestSubmitAnswers,
  ): Observable<unknown> {
    return this.clientRequestApiService.clientRequestSubmitAnswers(submitAnswerPayload).pipe(
      switchMap((response) => {
        return this.store.dispatch([
          new InsertAnswersIntoRequestItems(response, submitAnswerPayload),
          new ClientRequestSubmitAnswersSuccess(),
        ]);
      }),
    );
  }

  @Action([UndoFileCategorization])
  undoFileCategorization(
    _: StateContext<ClientRequestsStateModel>,
    { clientRequestDocumentFilesCategorizePayload }: UndoFileCategorization,
  ): Observable<unknown> {
    this._setSelectedRequestAsModifiedIfNotAlready();
    const categorizePayload: ClientRequestDocumentFilesCategorizePayload = {
      ...clientRequestDocumentFilesCategorizePayload,
      clientRequestDocumentFileCategorizeList: clientRequestDocumentFilesCategorizePayload.clientRequestDocumentFileCategorizeList.map(
        (file) => ({
          fileId: file.fileId,
          documentId: file.documentId,
        }),
      ),
    };

    return this.clientRequestApiService.undoFileCategorization(categorizePayload).pipe(
      switchMap((_) => {
        const selectedRequestId = this.store.selectSnapshot(ClientRequestsState.getSlices.selectedRequestId);
        const categorizedFilesToRemove = clientRequestDocumentFilesCategorizePayload.clientRequestDocumentFileCategorizeList.reduce(
          (acc, { documentId, fileId }) => ({ ...acc, [documentId]: (acc[documentId] ?? []).concat(fileId) }),
          {} as Record<string, number[]>,
        );

        return this.store.dispatch([
          new UnCategorizeFilesFromClientRequestDocuments(selectedRequestId, categorizedFilesToRemove),
          new RemoveNewlyUploadedFiles(
            clientRequestDocumentFilesCategorizePayload.clientRequestDocumentFileCategorizeList.map((file) => file.fileName),
          ),
          new UndoFileCategorizationSuccess(),
        ]);
      }),
    );
  }

  private _setSelectedRequestAsModifiedIfNotAlready(): void {
    const hasSelectedRequestBeenModified = this.store.selectSnapshot(ClientRequestsState.getSlices.hasSelectedRequestBeenModified);
    if (!hasSelectedRequestBeenModified) {
      this.store.dispatch(new SetSelectedRequestAsModified());
    }
  }

  private _getSubmitFileReadableErrorMessage(error: unknown): string {
    const fallback = 'We could not finish submitting this file. Please try again.';
    const candidate = (error as { message?: unknown })?.message;
    return typeof candidate === 'string' && candidate.trim().length > 0 ? candidate : fallback;
  }

  @Action([SetSelectedRequestAsModified])
  setSelectedRequestAsModified({ patchState }: StateContext<ClientRequestsStateModel>): void {
    patchState({ hasSelectedRequestBeenModified: true });
  }

  @Action([SetSelectedRequestItemId])
  setSelectedRequestItemId({ patchState }: StateContext<ClientRequestsStateModel>, { requestItemId }: SetSelectedRequestItemId): void {
    patchState({ selectedRequestItemId: requestItemId });
  }

  @Action([ClearSelectedRequestAsModified])
  clearSelectedRequestAsModified({ patchState }: StateContext<ClientRequestsStateModel>): void {
    patchState({ hasSelectedRequestBeenModified: undefined });
  }

  @Action([AppendNewlyUploadedFiles])
  appendNewlyCategorizedFiles({ setState }: StateContext<ClientRequestsStateModel>, { fileNames }: AppendNewlyUploadedFiles): void {
    setState(patch<ClientRequestsStateModel>({ newlyUpdatedFiles: append(fileNames) }));
  }

  @Action([RemoveNewlyUploadedFiles])
  removeNewlyCategorizedFiles({ setState }: StateContext<ClientRequestsStateModel>, { fileNames }: RemoveNewlyUploadedFiles): void {
    setState(patch<ClientRequestsStateModel>({ newlyUpdatedFiles: removeItem((file) => fileNames.includes(file)) }));
  }

  @Action([SubmitClientRequest])
  submitClientRequest({ dispatch }: StateContext<ClientRequestsStateModel>): Observable<unknown> {
    const id = this.store.selectSnapshot(ClientRequestsState.getSlices.selectedRequestId);

    // Snapshot pre-submit item state + actor id so we can emit per-item tracking events with a
    // meaningful `old_status` and `is_resubmitted` once the API succeeds.
    let preSubmitItems: { sectionId: number; itemId: number; item: RequestItem }[] = [];
    try {
      preSubmitItems = this.store.selectSnapshot(ClientRequestsState.getSelectedClientRequestItems) ?? [];
    } catch (error) {
      this.logger.warn('ClientRequestsState :: submitClientRequest :: failed to snapshot pre-submit items', error);
    }

    return this.clientRequestApiService.submitClientRequest(id).pipe(
      tap(() => {
        if (preSubmitItems.length > 0) {
          this.clientRequestPendoTrackingService.trackRequestItemsSubmitted(id, preSubmitItems);
        }
      }),
      catchError((error) => {
        this.clientRequestPendoTrackingService.trackRequestItemsSubmitFailed(id, preSubmitItems, error);
        return throwError(() => error);
      }),
      switchMap(() => dispatch(new SubmitClientRequestSuccess())),
    );
  }

  @Action([ReplaceFileForClientRequest])
  replaceFileForClientRequest(
    { dispatch }: StateContext<ClientRequestsStateModel>,
    { clientRequestItemId, originalFileId, externalDmsFileId }: ReplaceFileForClientRequest,
  ): Observable<unknown> {
    const payload: ReplaceClientRequestItemSubmissionFilePayload = {
      clientRequestItemId,
      originalFileId,
      externalDmsFileId,
    };

    return this.clientRequestApiService.replaceFile(payload).pipe(
      tap(() => {
        // Refresh both client request details and metadata with REFRESH strategy
        const currentRequestId = this.store.selectSnapshot(ClientRequestsState.getSlices.selectedRequestId);
        if (currentRequestId) {
          dispatch(new ReplaceFileForClientRequestSuccess(currentRequestId, originalFileId, externalDmsFileId));
        }
      }),
      // Let errors bubble up to component
    );
  }

  @Action([CreateSecureLink])
  createSecureLink(_: StateContext<ClientRequestsStateModel>, { createSecureLinkData }: CreateSecureLink) {
    const grants = createSecureLinkData.grants;

    const createSecureLinkPayload: CreateSecureLinkPayload = {
      grants,
      contactId: createSecureLinkData.contactId,
      emailAddress: createSecureLinkData.emailAddress,
      displayName: createSecureLinkData.displayName,
    };

    return this.clientRequestApiService.createSecureLink(createSecureLinkPayload).pipe(
      switchMap((result) => {
        const secureLinkSendEmailPayload: SecureLinkSendEmailPayload = {
          secureLinkUuid: result.data.uuid,
          reassignedClientRequestItemIds: result.data.grants.map((grant) => grant.entityId),
          contactMessage: createSecureLinkData.message,
          clientId: createSecureLinkData.clientId,
        };

        const assigneesId = grants.map((grant) => grant.entityId);
        const secureLinkUser: SecureLinkUser = {
          uuid: result.data.uuid,
          emailAddress: createSecureLinkData.emailAddress,
          displayName: createSecureLinkData.displayName,
        };

        return combineLatest([
          this.store.dispatch([
            new SecureLinkSendEmail(secureLinkSendEmailPayload),
            new SetSecureLinkUserForRequestItem(
              secureLinkUser,
              assigneesId,
              this.store.selectSnapshot(ClientRequestsState.getSlices.selectedRequestId),
            ),
          ]),
        ]);
      }),
    );
  }

  @Action([UpdateSecureLinkGrants])
  updateSecureLinkGrants(_: StateContext<ClientRequestsStateModel>, { updateSecureLinkGrantsData }: UpdateSecureLinkGrants) {
    const updateSecureLinkGrantsPayload: UpdateSecureLinkGrantsPayload = {
      UUID: updateSecureLinkGrantsData.UUID,
      grants: updateSecureLinkGrantsData.grants.map((grant) => {
        const newGrant = {
          id: grant.id,
          entityId: grant.entityId,
          secureLinkEntityType: SecureLinkEntityType.CLIENT_REQUEST_ITEM_ASSIGNEE,
          active: grant.active,
        };

        return newGrant;
      }),
    };

    return this.clientRequestApiService.updateSecureLinkGrants(updateSecureLinkGrantsPayload).pipe(
      switchMap(() => {
        const secureLinkSendEmailPayload: SecureLinkSendEmailPayload = {
          secureLinkUuid: updateSecureLinkGrantsData.UUID,
          reassignedClientRequestItemIds: updateSecureLinkGrantsData.grants.map((grant) => grant.entityId),
          contactMessage: updateSecureLinkGrantsData.message,
          clientId: updateSecureLinkGrantsData.clientId,
        };

        const grants = updateSecureLinkGrantsData.grants;
        const assigneesIdToAdd = grants.filter((grant) => grant.active).map((grant) => grant.entityId);
        const assigneesIdToRemove = grants.filter((grant) => !grant.active).map((grant) => grant.entityId);

        const actions: unknown[] = [new SecureLinkSendEmail(secureLinkSendEmailPayload)];

        if (assigneesIdToRemove.length) {
          actions.push(
            new RemoveSecureLinkUserForRequestItem(
              updateSecureLinkGrantsData.secureLinkUser,
              assigneesIdToRemove,
              this.store.selectSnapshot(ClientRequestsState.getSlices.selectedRequestId),
            ),
          );
        }

        if (assigneesIdToAdd.length) {
          actions.push(
            new SetSecureLinkUserForRequestItem(
              updateSecureLinkGrantsData.secureLinkUser,
              assigneesIdToAdd,
              this.store.selectSnapshot(ClientRequestsState.getSlices.selectedRequestId),
            ),
          );
        }

        return this.store.dispatch(actions);
      }),
    );
  }

  @Action([SecureLinkSendEmail])
  secureLinkSendEmail(_: StateContext<ClientRequestsStateModel>, { secureLinkSendEmailPayload }: SecureLinkSendEmail) {
    return this.clientRequestApiService.secureLinkSendEmail(secureLinkSendEmailPayload);
  }

  @Action([LogOut])
  logOut(_: StateContext<ClientRequestsStateModel>): void {
    localStorage.removeItem(CLIENT_REQUEST_FILTERS_KEY);
  }

  @Action([ResetState])
  resetState({ setState }: StateContext<ClientRequestsStateModel>): void {
    setState(DEFAULT_CLIENT_REQUESTS_STATE);
  }

  private _handleFilesUploadedDirectlyToClientRequest(
    filesUploadedDirectlyToClientRequest: ClientRequestFileSubmissionResult[],
    context: StateContext<ClientRequestsStateModel>,
    clientRequestId: number,
  ) {
    return merge(
      filesUploadedDirectlyToClientRequest.map(({ fileId }) =>
        this.clientRequestApiService.listenForDocumentFileCategorizations(fileId).pipe(
          takeWhile((submission) => submission.status === RequestSubmissionFileStatus.CATEGORIZING, true),
          last(),
          timeout({
            each: FILE_CATEGORIZATION_TIMEOUT,
            with: () => throwError(() => new CategorizationTimeoutError('File categorization timed out', fileId)),
          }),
          catchError((error: CategorizationTimeoutError, caught) => {
            this.logger.error('ClientRequestsState : SubmitFilesForClientRequest : File categorization timed out', error);

            const stagedJobs = this.store.selectSnapshot(ClientRequestsState.getStagedFileJobs);

            const matchingSubmission = filesUploadedDirectlyToClientRequest.find((f) => f.fileId === error.fileId);
            const jobToRemove = matchingSubmission ? stagedJobs.find((job) => job.identity === matchingSubmission.fileDmsId) : null;

            if (jobToRemove) {
              // TODO: Refactor newlyUpdatedFiles to use IDs instead of fileName values
              const fileName = getOriginalBoxFileName(jobToRemove.data.fileName);

              context.patchState({
                stagedFileJobIds: context.getState().stagedFileJobIds.filter((id) => id !== jobToRemove.jobId),
                newlyUpdatedFiles: context.getState().newlyUpdatedFiles.filter((name) => name !== fileName),
              });

              context.dispatch(
                new UploadFileJobCategorizationDisplayStatus(
                  jobToRemove.jobId,
                  ClientRequestDocumentFileDisplayStatus.CATEGORIZATION_TIMEOUT,
                ),
              );
            }

            return caught.pipe(
              take(1),
              switchMap(() => EMPTY),
            );
          }),
          tap((categorizationStatus) => {
            this.logger.debug('ClientRequestsState : SubmitFilesForClientRequest : File categorization complete', categorizationStatus);

            const requestSubmissionFile = {
              id: categorizationStatus.id,
              clientRequestId,
              fileId: categorizationStatus.fileId,
              status: categorizationStatus.status,
              file: categorizationStatus.file,
            };

            const stagedJobs = this.store.selectSnapshot(ClientRequestsState.getStagedFileJobs);
            const jobToRemove = stagedJobs.find((job) => job.data.fileExternalId === categorizationStatus.file.dmsId);

            if (jobToRemove) {
              context.patchState({
                stagedFileJobIds: context.getState().stagedFileJobIds.filter((id) => id !== jobToRemove.jobId),
              });
              context.dispatch(
                new UploadFileJobCategorizationDisplayStatus(
                  jobToRemove.jobId,
                  RequestSubmissionFileStatusToClientRequestDocumentFileDisplayStatus[requestSubmissionFile.status],
                ),
              );
            }

            context.dispatch([
              new InsertUncategorizedFilesIntoClientRequest(clientRequestId, [requestSubmissionFile]),
              new PatchClientRequestState(
                patch({
                  metadata: updateItem(
                    (documentRequestMetadataModel) => documentRequestMetadataModel.data.clientRequestId === clientRequestId,
                    patch({
                      data: patch({
                        files: updateItem(
                          (requestSubmissionFileRecord) => requestSubmissionFileRecord.fileId === categorizationStatus.fileId,
                          requestSubmissionFile,
                        ),
                      }),
                    }),
                  ),
                }),
              ),
            ]);
          }),
          filter((categorizationStatus) => categorizationStatus.status === RequestSubmissionFileStatus.AUTO_CATEGORIZED),
          switchMap((categorizationStatus) =>
            this.clientRequestApiService.getClientRequestItemSubmissionFiles(categorizationStatus.fileId),
          ),
          switchMap((submission) => {
            return context.dispatch([new InsertCategorizedFilesIntoClientRequest(clientRequestId, [submission])]);
          }),
          switchMap(() => context.dispatch([new AutoCategorizationSuccess()])),
        ),
      ),
    );
  }

  @Action([
    ClientRequestLinkFilesToDocumentsSuccess,
    CategorizeFileIntoDocumentSuccess,
    ClientRequestSubmitAnswersSuccess,
    UndoFileCategorizationSuccess,
    SubmitFilesForClientRequestSuccess,
    SubmitClientRequestSuccess,
    RemoveClientRequestFilesSuccess,
    ReplaceFileForClientRequestSuccess,
    AutoCategorizationSuccess,
  ])
  refreshClientRequestData({ dispatch }: StateContext<ClientRequestsStateModel>): Observable<unknown> {
    const clientRequestId = this.store.selectSnapshot(ClientRequestsState.getSlices.selectedRequestId);
    const restrictedRequestItemsIds = this.store.selectSnapshot(ClientRequestsState.getSlices.restrictedRequestItemsIds);
    return this.store
      .dispatch(new GetClientRequestDetails(clientRequestId, LoadableArrayLoadStrategy.REFRESH, restrictedRequestItemsIds))
      .pipe(
        switchMap(() => {
          return combineLatest([
            this.store.dispatch(new GetClientRequestMetadata(clientRequestId, LoadableArrayLoadStrategy.REFRESH)),
            this.store.dispatch(new GetQuestionnaireResponses(clientRequestId, LoadableArrayLoadStrategy.REFRESH)),
          ]);
        }),
      );
  }

  @Action(GetClientRequestDetailsSuccess)
  initializeNotApplicableItems(
    { getState, patchState }: StateContext<ClientRequestsStateModel>,
    { clientRequestId, details }: GetClientRequestDetailsSuccess,
  ): void {
    const allItems = details.sections.flatMap((section) => section.items);

    // Build unified notApplicableItems with both itemId and note
    const notApplicableItems = allItems
      .filter((item) => item.isNotApplicable)
      .map((item) => ({
        itemId: item.id,
        note: item.notApplicableReason,
      }));

    patchState({
      notApplicableItems: {
        ...getState().notApplicableItems,
        [clientRequestId]: notApplicableItems,
      },
    });
  }

  @Action(SetRequestItemAsNotApplicable)
  setRequestItemAsNotApplicable(
    { getState, patchState }: StateContext<ClientRequestsStateModel>,
    { requestItemId, isNotApplicable, notApplicableReason }: SetRequestItemAsNotApplicable,
  ): Observable<unknown> {
    const state = getState();
    const clientRequestId = this.store.selectSnapshot(ClientRequestsState.getSlices.selectedRequestId);
    const currentItems = state.notApplicableItems[clientRequestId] || [];

    // Optimistic update - unified structure
    let updatedItems: Array<{ itemId: number; note: string | null }>;
    if (isNotApplicable) {
      // Add or update the item
      const existingItemIndex = currentItems.findIndex((item) => item.itemId === requestItemId);
      if (existingItemIndex >= 0) {
        // Update existing item with new note
        updatedItems = [...currentItems];
        updatedItems[existingItemIndex] = { itemId: requestItemId, note: notApplicableReason || null };
      } else {
        // Add new item
        updatedItems = [...currentItems, { itemId: requestItemId, note: notApplicableReason || null }];
      }
    } else {
      // Remove item from not applicable list
      updatedItems = currentItems.filter((item) => item.itemId !== requestItemId);
    }

    patchState({
      notApplicableItems: {
        ...state.notApplicableItems,
        [clientRequestId]: updatedItems,
      },
    });

    // API call with rollback on error
    return this.clientRequestApiService
      .setClientRequestItemNotApplicable({
        clientRequestItemId: requestItemId,
        isNotApplicable,
        notApplicableReason,
      })
      .pipe(
        tap(() => {
          // Dispatch success action to trigger targeted sync in commonClientRequests state
          this.store.dispatch(new SetClientRequestItemNotApplicableSuccess(requestItemId));
        }),
        catchError((error) => {
          // Rollback optimistic update
          patchState({
            notApplicableItems: {
              ...getState().notApplicableItems,
              [clientRequestId]: currentItems,
            },
          });

          this.snackbarService.open('Failed to update not applicable status', SnackbarType.ERROR);

          this.logger.error('SetRequestItemAsNotApplicable error:', error);
          return of(null);
        }),
      );
  }

  @Action([UpdateRequestItemFilterSelection])
  updateRequestItemFilterSelection(
    { patchState }: StateContext<ClientRequestsStateModel>,
    { path, value }: UpdateRequestItemFilterSelection,
  ): void {
    const existingFilters = this.store.selectSnapshot(ClientRequestsState.getSlices.requestItemFilters);
    const updatedFilters = value ? upsertNestedRecord(existingFilters, path, value) : removeNestedRecord(existingFilters, path);
    const trimmedFilters = trimObject(updatedFilters);

    patchState({ requestItemFilters: trimmedFilters });

    // Only persist to localStorage if there are filters set. Trying to be lean.
    if (isEmpty(trimmedFilters) && localStorage.getItem(CLIENT_REQUEST_ITEM_FILTERS_KEY)) {
      localStorage.removeItem(CLIENT_REQUEST_ITEM_FILTERS_KEY);
    } else {
      localStorage.setItem(CLIENT_REQUEST_ITEM_FILTERS_KEY, JSON.stringify(trimmedFilters));
    }
  }

  @Action([ClearRequestItemFilterSelection])
  clearRequestItemFilterSelection(
    { patchState }: StateContext<ClientRequestsStateModel>,
    { requestId }: ClearRequestItemFilterSelection,
  ): void {
    const existingFilters = this.store.selectSnapshot(ClientRequestsState.getSlices.requestItemFilters);
    const updatedFilters = removeNestedRecord(existingFilters, [requestId]);
    const trimmedFilters = trimObject(updatedFilters);

    patchState({ requestItemFilters: trimmedFilters });

    // Only persist to localStorage if there are filters set. Trying to be lean.
    if (isEmpty(trimmedFilters) && localStorage.getItem(CLIENT_REQUEST_ITEM_FILTERS_KEY)) {
      localStorage.removeItem(CLIENT_REQUEST_ITEM_FILTERS_KEY);
    } else {
      localStorage.setItem(CLIENT_REQUEST_ITEM_FILTERS_KEY, JSON.stringify(trimmedFilters));
    }
  }
}
